import { useCallback, useEffect, useState } from 'react';
import {
  EVENT_STATUS_LABELS, EVENT_TRANSITIONS,
  REGISTRATION_STATUS_LABELS, REGISTRATION_TRANSITIONS,
  publishBlockers, MAX_PAGE_SIZE,
  type EventPmiRow, type EventStatus, type EventRegistrationRow, type RegistrationStatus,
  type FormTemplateRow, type FormVersionRow, type FormSubmissionView,
  type DonationRow, type Paginated,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { Icon } from '../components/icons';
import { FileUpload } from '../components/FileUpload';

interface Named { id: string; code: string; name: string }

/** The full drive row, as `GET /events/:id` returns it. */
interface EventDetail {
  id: string; branchId: string; code: string; name: string;
  description: string | null; imageUrl: string | null;
  venueName: string | null; address: string | null; city: string | null;
  latitude: string | null; longitude: string | null;
  startsAt: string; endsAt: string;
  quota: number | null; status: EventStatus; formVersionId: string | null;
}

const EMPTY = {
  branchId: '', code: '', name: '', description: '',
  venueName: '', address: '', city: '', imageUrl: '',
  latitude: '', longitude: '',
  startsAt: '', endsAt: '', quota: '',
  /** UI only — it picks which versions the version select offers. */
  templateId: '', formVersionId: '',
};

const fmtWhen = (iso: string) => new Date(iso).toLocaleString('id-ID', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** ISO → the `YYYY-MM-DDTHH:mm` a datetime-local input expects, in the browser's zone. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_PILL: Record<EventStatus, string> = {
  draft: 'pill',
  published: 'pill ok',
  closed: 'pill',
  cancelled: 'pill accent',
};

const REG_PILL: Record<RegistrationStatus, string> = {
  registered: 'pill',
  attended: 'pill warn',
  donated: 'pill ok',
  rejected: 'pill accent',
  no_show: 'pill',
  cancelled: 'pill',
};

export function Events() {
  const { user: me, can } = useAuth();
  const list = usePagedList<EventPmiRow>('/events');

  const [branches, setBranches] = useState<Named[]>([]);
  const [templates, setTemplates] = useState<FormTemplateRow[]>([]);
  const [versions, setVersions] = useState<FormVersionRow[]>([]);

  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [formLabel, setFormLabel] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<EventRegistrationRow[]>([]);

  const [open, setOpen] = useState(false);
  /** Set when the dialog is editing rather than creating. */
  const [editing, setEditing] = useState<EventDetail | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** A registration transition that needs more than a click. */
  const [regAction, setRegAction] = useState<
    { reg: EventRegistrationRow; status: RegistrationStatus } | null
  >(null);
  const [regReason, setRegReason] = useState('');
  const [regDonationId, setRegDonationId] = useState('');
  const [regDonations, setRegDonations] = useState<DonationRow[]>([]);
  const [regLoading, setRegLoading] = useState(false);

  const [submission, setSubmission] = useState<FormSubmissionView | null>(null);

  useEffect(() => {
    (async () => {
      const [b, t] = await Promise.all([
        api.get<Paginated<Named>>(`/branches?pageSize=${MAX_PAGE_SIZE}`).catch(() => null),
        api.get<Paginated<FormTemplateRow>>(`/forms/templates?purpose=donor&pageSize=${MAX_PAGE_SIZE}`).catch(() => null),
      ]);
      if (b) setBranches(b.items);
      if (t) setTemplates(t.items);
    })();
  }, []);

  /** Versions belong to a template, so the second select is refilled when the first moves. */
  useEffect(() => {
    if (!form.templateId) { setVersions([]); return; }
    let cancelled = false;
    (async () => {
      const rows = await api.get<FormVersionRow[]>(`/forms/templates/${form.templateId}/versions`)
        .catch(() => []);
      if (cancelled) return;
      setVersions(rows);
      // Default to the newest version — the one a new drive almost always wants.
      setForm((f) => (f.formVersionId ? f : { ...f, formVersionId: rows[0]?.id ?? '' }));
    })();
    return () => { cancelled = true; };
  }, [form.templateId]);

  const openDetail = useCallback(async (id: string) => {
    setError(null);
    setNotice(null);
    try {
      const [e, regs] = await Promise.all([
        api.get<EventDetail>(`/events/${id}`),
        api.get<EventRegistrationRow[]>(`/events/${id}/registrations`).catch(() => []),
      ]);
      setDetail(e);
      setRegistrations(regs);
      setFormLabel(null);
      if (e.formVersionId) {
        const v = await api.get<{ templateName: string; versionNo: number }>(
          `/forms/versions/${e.formVersionId}`,
        ).catch(() => null);
        if (v) setFormLabel(`${v.templateName} · v${v.versionNo}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat kegiatan.');
    }
  }, []);

  function openCreate() {
    // A branch operator can only write to their own branch, so it is preselected and locked.
    setEditing(null);
    setForm({ ...EMPTY, branchId: me?.branchId ?? '' });
    setVersions([]);
    setFormError(null);
    setOpen(true);
  }

  async function openEdit(e: EventDetail) {
    setEditing(e);
    setFormError(null);
    // Resolve which template the drive's version belongs to, so the version select has
    // something to list.
    let templateId = '';
    if (e.formVersionId) {
      const v = await api.get<{ templateId: string }>(`/forms/versions/${e.formVersionId}`)
        .catch(() => null);
      templateId = v?.templateId ?? '';
    }
    setForm({
      branchId: e.branchId, code: e.code, name: e.name,
      description: e.description ?? '',
      venueName: e.venueName ?? '', address: e.address ?? '', city: e.city ?? '',
      imageUrl: e.imageUrl ?? '',
      latitude: e.latitude ?? '', longitude: e.longitude ?? '',
      startsAt: toLocalInput(e.startsAt), endsAt: toLocalInput(e.endsAt),
      quota: e.quota === null ? '' : String(e.quota),
      templateId, formVersionId: e.formVersionId ?? '',
    });
    setOpen(true);
  }

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    setSaving(true);
    setFormError(null);
    const payload = {
      branchId: form.branchId,
      code: form.code,
      name: form.name,
      description: form.description || null,
      imageUrl: form.imageUrl || null,
      venueName: form.venueName || null,
      address: form.address || null,
      city: form.city || null,
      latitude: form.latitude === '' ? null : Number(form.latitude),
      longitude: form.longitude === '' ? null : Number(form.longitude),
      startsAt: new Date(form.startsAt).toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      quota: form.quota === '' ? null : Number(form.quota),
      formVersionId: form.formVersionId || null,
    };
    try {
      if (editing) {
        await api.patch(`/events/${editing.id}`, payload);
        setNotice(`Kegiatan ${form.code} diperbarui.`);
        await openDetail(editing.id);
      } else {
        await api.post('/events', payload);
        setNotice(`Kegiatan ${form.code} dibuat sebagai draf. Publikasikan setelah lokasi dan form lengkap.`);
      }
      setOpen(false);
      list.refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan kegiatan.');
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(e: EventDetail, status: EventStatus) {
    setError(null);
    setNotice(null);
    try {
      await api.post(`/events/${e.id}/status`, { status });
      setNotice(`Kegiatan ${e.code} sekarang ${EVENT_STATUS_LABELS[status].toLowerCase()}.`);
      await openDetail(e.id);
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mengubah status kegiatan.');
    }
  }

  /** Simple transitions go straight through; the two that need input open a dialog. */
  async function moveRegistration(reg: EventRegistrationRow, status: RegistrationStatus) {
    if (status === 'rejected' || status === 'donated') {
      setRegReason('');
      setRegDonationId('');
      setRegDonations([]);
      setRegAction({ reg, status });
      if (status === 'donated') {
        setRegLoading(true);
        const d = await api.get<Paginated<DonationRow>>(
          `/donations?donorId=${reg.donorId}&pageSize=${MAX_PAGE_SIZE}`,
        ).catch(() => null);
        if (d) {
          setRegDonations(d.items);
          setRegDonationId(d.items[0]?.id ?? '');
        }
        setRegLoading(false);
      }
      return;
    }
    await commitRegistration(reg, status, {});
  }

  async function commitRegistration(
    reg: EventRegistrationRow,
    status: RegistrationStatus,
    extra: { rejectionReason?: string; donationId?: string },
  ) {
    setError(null);
    try {
      await api.post(`/events/registrations/${reg.id}/status`, { status, ...extra });
      setRegAction(null);
      setNotice(`${reg.donorName}: ${REGISTRATION_STATUS_LABELS[status].toLowerCase()}.`);
      if (detail) await openDetail(detail.id);
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mengubah status pendaftaran.');
      setRegAction(null);
    }
  }

  async function viewAssessment(submissionId: string) {
    setError(null);
    try {
      setSubmission(await api.get<FormSubmissionView>(`/forms/submissions/${submissionId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat asesmen.');
    }
  }

  const blockers = detail ? publishBlockers(detail) : [];
  const writable = can('event:write');
  /** A branch operator may only write to their own branch, so the select is fixed. */
  const lockedToBranch = Boolean(me?.branchId);

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Kegiatan Donor</h2>
        <p>
          Kegiatan dibuat sebagai draf. Sebelum bisa dipublikasikan ia wajib punya
          koordinat lokasi dan versi form asesmen — tanpa koordinat pendonor tidak bisa
          menemukan lokasinya, tanpa form petugas tidak punya kuesioner. Aturan itu
          dijaga basis data, bukan hanya layar ini.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari kode, nama, tempat, atau kota…"
        action={writable ? { label: 'Buat kegiatan', onClick: openCreate } : undefined}
        empty="Belum ada kegiatan donor."
        emptyFiltered="Tidak ada kegiatan yang cocok dengan pencarian."
        columns={['Kode', 'Nama', 'Cabang', 'Jadwal', { label: 'Peserta', align: 'right' }, 'Status', '']}
      >
        {list.items.map((e) => (
          <tr key={e.id}>
            <td className="mono">{e.code}</td>
            <td>
              {e.name}
              {e.city && <div className="muted" style={{ fontSize: 12.5 }}>{e.city}</div>}
            </td>
            <td className="mono muted">{e.branchCode}</td>
            <td className="mono" style={{ fontSize: 12.5 }}>{fmtWhen(e.startsAt)}</td>
            <td className="mono" style={{ textAlign: 'right' }}>
              {e.registeredCount}{e.quota !== null ? ` / ${e.quota}` : ''}
            </td>
            <td>
              <span className={STATUS_PILL[e.status]}>{EVENT_STATUS_LABELS[e.status]}</span>
              {e.status === 'draft' && publishBlockers(e).length > 0 && (
                <span className="pill warn" style={{ marginLeft: 6 }} title={`Kurang: ${publishBlockers(e).join(', ')}`}>
                  belum lengkap
                </span>
              )}
            </td>
            <td>
              <button type="button" onClick={() => void openDetail(e.id)}>Kelola</button>
            </td>
          </tr>
        ))}
      </DataTable>

      {detail && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3>{detail.name}</h3>
              <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: 13 }}>
                <span className="mono">{detail.code}</span> ·{' '}
                <span className={STATUS_PILL[detail.status]}>{EVENT_STATUS_LABELS[detail.status]}</span>
              </p>
            </div>
            <button type="button" className="icon-btn" onClick={() => setDetail(null)} aria-label="Tutup detail">
              <Icon name="close" size={18} />
            </button>
          </div>

          {blockers.length > 0 && detail.status === 'draft' && (
            <div className="msg error">
              Belum bisa dipublikasikan — masih kurang <strong>{blockers.join(' dan ')}</strong>.
            </div>
          )}

          <div className="grid-2">
            <div className="field">
              <span className="label">Jadwal</span>
              <span>{fmtWhen(detail.startsAt)} — {fmtWhen(detail.endsAt)}</span>
            </div>
            <div className="field">
              <span className="label">Lokasi</span>
              <span>{detail.venueName ?? '—'}{detail.city ? `, ${detail.city}` : ''}</span>
            </div>
            <div className="field">
              <span className="label">Koordinat</span>
              <span className="mono">
                {detail.latitude ? `${detail.latitude}, ${detail.longitude}` : '— belum diisi'}
              </span>
            </div>
            <div className="field">
              <span className="label">Form asesmen</span>
              <span>{formLabel ?? '— belum dipilih'}</span>
            </div>
            <div className="field">
              <span className="label">Kuota</span>
              <span className="mono">{detail.quota === null ? 'tanpa batas' : detail.quota}</span>
            </div>
            <div className="field">
              <span className="label">Alamat</span>
              <span>{detail.address ?? '—'}</span>
            </div>
          </div>

          {detail.description && (
            <p style={{ margin: 0, fontSize: 14 }}>{detail.description}</p>
          )}

          {writable && (
            <div className="row">
              <button type="button" onClick={() => void openEdit(detail)}>Ubah</button>
              {EVENT_TRANSITIONS[detail.status].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={s === 'published' ? 'primary' : s === 'cancelled' ? 'danger' : undefined}
                  disabled={s === 'published' && blockers.length > 0}
                  title={s === 'published' && blockers.length > 0 ? `Kurang: ${blockers.join(', ')}` : undefined}
                  onClick={() => void setStatus(detail, s)}
                >
                  {s === 'published' ? 'Publikasikan'
                    : s === 'closed' ? 'Tutup kegiatan'
                    : 'Batalkan'}
                </button>
              ))}
            </div>
          )}

          <div>
            <h3 style={{ marginBottom: '0.6rem' }}>
              Peserta <span className="muted" style={{ fontWeight: 400 }}>({registrations.length})</span>
            </h3>
            {registrations.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                Belum ada pendonor yang mendaftar.
              </p>
            ) : (
              <div className="card table-card">
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Pendonor</th><th>NIK</th><th>Golongan</th>
                        <th>Daftar</th><th>Status</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((r) => (
                        <tr key={r.id}>
                          <td>{r.donorName}</td>
                          <td className="mono muted">…{r.donorNikLast4}</td>
                          <td className="mono">
                            {r.donorAbo ? `${r.donorAbo}${r.donorRh === 'positive' ? '+' : '−'}` : '—'}
                          </td>
                          <td className="mono" style={{ fontSize: 12.5 }}>{fmtWhen(r.registeredAt)}</td>
                          <td>
                            <span className={REG_PILL[r.status]} title={r.rejectionReason ?? undefined}>
                              {REGISTRATION_STATUS_LABELS[r.status]}
                            </span>
                          </td>
                          <td>
                            <div className="row" style={{ gap: '0.35rem' }}>
                              {r.submissionId && (
                                <button type="button" onClick={() => void viewAssessment(r.submissionId!)}>
                                  Asesmen
                                </button>
                              )}
                              {writable && REGISTRATION_TRANSITIONS[r.status].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  className={s === 'donated' ? 'primary' : s === 'rejected' ? 'danger' : undefined}
                                  onClick={() => void moveRegistration(r, s)}
                                >
                                  {REGISTRATION_STATUS_LABELS[s]}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        open={open}
        title={editing ? 'Ubah kegiatan' : 'Buat kegiatan donor'}
        description={editing
          ? 'Mengubah jadwal kegiatan yang sudah dipublikasikan tidak menariknya kembali jadi draf — pendonor sudah diberi tahu kegiatan itu ada.'
          : 'Kegiatan dibuat sebagai draf. Koordinat dan form asesmen bisa diisi sekarang atau menyusul, tapi keduanya wajib sebelum publikasi.'}
        onClose={() => setOpen(false)}
        onSubmit={save}
        size="wide"
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : editing ? 'Simpan perubahan' : 'Simpan draf'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}

          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="e-branch">Cabang penyelenggara</label>
              <select id="e-branch" value={form.branchId} required disabled={lockedToBranch}
                onChange={(ev) => setForm({ ...form, branchId: ev.target.value })}>
                <option value="">— pilih cabang —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="e-code">Kode kegiatan</label>
              <input id="e-code" value={form.code} required placeholder="DONOR-BDG-2026-01"
                onChange={(ev) => setForm({ ...form, code: ev.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="e-name">Nama kegiatan</label>
              <input id="e-name" value={form.name} required placeholder="Donor Darah Bersama Kampus"
                onChange={(ev) => setForm({ ...form, name: ev.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="e-desc">Deskripsi</label>
              <textarea id="e-desc" rows={3} value={form.description}
                onChange={(ev) => setForm({ ...form, description: ev.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="e-image">Gambar kegiatan</label>
              <FileUpload
                id="e-image"
                target="event-image"
                value={form.imageUrl}
                onChange={(url) => setForm({ ...form, imageUrl: url })}
                hint="Tampil di daftar kegiatan yang dilihat pendonor."
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="e-start">Mulai</label>
              <input id="e-start" type="datetime-local" value={form.startsAt} required
                onChange={(ev) => setForm({ ...form, startsAt: ev.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="e-end">Selesai</label>
              <input id="e-end" type="datetime-local" value={form.endsAt} required
                onChange={(ev) => setForm({ ...form, endsAt: ev.target.value })} />
            </div>

            <div className="field">
              <label className="label" htmlFor="e-venue">Nama tempat</label>
              <input id="e-venue" value={form.venueName} placeholder="Aula Kampus"
                onChange={(ev) => setForm({ ...form, venueName: ev.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="e-city">Kota</label>
              <input id="e-city" value={form.city}
                onChange={(ev) => setForm({ ...form, city: ev.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="e-address">Alamat</label>
              <input id="e-address" value={form.address}
                onChange={(ev) => setForm({ ...form, address: ev.target.value })} />
            </div>

            <div className="field">
              <label className="label" htmlFor="e-lat">Lintang</label>
              <input id="e-lat" value={form.latitude} inputMode="decimal" placeholder="-6.9034"
                onChange={(ev) => setForm({ ...form, latitude: ev.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="e-lng">Bujur</label>
              <input id="e-lng" value={form.longitude} inputMode="decimal" placeholder="107.6181"
                onChange={(ev) => setForm({ ...form, longitude: ev.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                Lintang dan bujur harus diisi bersamaan — satu tanpa yang lain membuat
                pencarian kegiatan terdekat salah tanpa memunculkan error.
              </p>
            </div>

            <div className="field">
              <label className="label" htmlFor="e-quota">Kuota peserta</label>
              <input id="e-quota" value={form.quota} inputMode="numeric" placeholder="kosong = tanpa batas"
                onChange={(ev) => setForm({ ...form, quota: ev.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="e-tpl">Form asesmen</label>
              <select id="e-tpl" value={form.templateId}
                onChange={(ev) => setForm({ ...form, templateId: ev.target.value, formVersionId: '' })}>
                <option value="">— pilih form —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.branchCode ? ` · ${t.branchCode}` : ' · nasional'}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="e-ver">Versi form</label>
              <select id="e-ver" value={form.formVersionId} disabled={versions.length === 0}
                onChange={(ev) => setForm({ ...form, formVersionId: ev.target.value })}>
                <option value="">— pilih versi —</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.versionNo} · {v.fieldCount} pertanyaan
                    {v.submissionCount > 0 ? ` · ${v.submissionCount} terkunci` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                Kegiatan menunjuk ke satu <strong>versi</strong>, bukan ke template. Kalau
                template diedit setelah kegiatan jalan, kuesioner yang dilihat pendonor
                tidak berubah.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={regAction !== null}
        title={regAction?.status === 'rejected' ? 'Tolak pendonor di lokasi' : 'Tandai sudah donor'}
        description={regAction?.status === 'rejected'
          ? 'Alasan wajib dicatat dan ikut ke jejak audit.'
          : 'Hubungkan pendaftaran ini ke donasi yang sudah dicatat, supaya jejak ketertelusuran kantong tersambung ke kegiatannya.'}
        onClose={() => setRegAction(null)}
        footer={
          <>
            <button type="button" onClick={() => setRegAction(null)}>Batal</button>
            <button
              type="button"
              className="primary"
              disabled={regLoading || (regAction?.status === 'rejected'
                ? regReason.trim() === ''
                : regDonationId === '')}
              onClick={() => {
                if (!regAction) return;
                void commitRegistration(regAction.reg, regAction.status,
                  regAction.status === 'rejected'
                    ? { rejectionReason: regReason.trim() }
                    : { donationId: regDonationId });
              }}
            >
              Simpan
            </button>
          </>
        }
      >
        {regAction && (
          <div className="stack" style={{ gap: '0.8rem' }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              <strong>{regAction.reg.donorName}</strong>{' '}
              <span className="mono muted">…{regAction.reg.donorNikLast4}</span>
            </p>

            {regAction.status === 'rejected' ? (
              <div className="field">
                <label className="label" htmlFor="r-reason">Alasan penolakan</label>
                <textarea id="r-reason" rows={3} value={regReason}
                  placeholder="mis. hemoglobin di bawah batas"
                  onChange={(ev) => setRegReason(ev.target.value)} />
              </div>
            ) : regLoading ? (
              <p className="muted" style={{ margin: 0 }}>Memuat donasi pendonor ini…</p>
            ) : regDonations.length === 0 ? (
              <div className="msg error">
                Pendonor ini belum punya donasi yang tercatat. Catat donasinya dulu di
                halaman Donasi, lalu kembali ke sini untuk menghubungkannya.
              </div>
            ) : (
              <div className="field">
                <label className="label" htmlFor="r-donation">Donasi</label>
                <select id="r-donation" value={regDonationId}
                  onChange={(ev) => setRegDonationId(ev.target.value)}>
                  {regDonations.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.donationNumber} · {fmtWhen(d.collectedAt)} · {d.volumeMl} ml
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={submission !== null}
        title="Hasil asesmen"
        description={submission
          ? `${submission.templateName} · versi ${submission.versionNo} · diisi ${fmtWhen(submission.submittedAt)}`
          : undefined}
        onClose={() => setSubmission(null)}
        footer={<button type="button" onClick={() => setSubmission(null)}>Tutup</button>}
      >
        {submission && (
          <div className="stack" style={{ gap: '0.8rem' }}>
            <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
              Label pertanyaan di bawah berasal dari versi yang benar-benar diisi pendonor,
              bukan dari versi terbaru — itulah gunanya form diversikan.
            </p>
            {submission.answers.map((a) => (
              <div className="field" key={a.key}>
                <span className="label">{a.label}</span>
                <span>
                  {a.value === null ? <span className="muted">tidak dijawab</span>
                    : typeof a.value === 'boolean' ? (a.value ? 'Ya' : 'Tidak')
                    : Array.isArray(a.value) ? a.value.join(', ')
                    : String(a.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
